import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';

import {
  PoPageModule,
  PoButtonModule,
  PoFieldModule,
  PoTableModule,
  PoTableColumn,
  PoTableAction,
  PoPageAction,
  PoPageFilter,
  PoModalModule,
  PoModalComponent,
  PoModalAction,
  PoNotificationService,
  PoDialogService
} from '@po-ui/ng-components';

import { CNPJ_MASK, CNPJ_PATTERN, isValidCnpj } from '../../core/utils/validators';
import { Fornecedor, FornecedoresOcultoService } from './fornecedores-ocultos.service';

@Component({
  selector: 'app-fornecedores-ocultos',
  imports: [
    CommonModule,
    FormsModule,
    PoPageModule,
    PoButtonModule,
    PoFieldModule,
    PoTableModule,
    PoModalModule
  ],
  templateUrl: './fornecedores-ocultos.html',
  styleUrls: ['./fornecedores-ocultos.css'],
})
export class FornecedoresOcultosComponent implements OnInit {
  @ViewChild('formModal', { static: true }) formModal!: PoModalComponent;

  loading = false;
  loadingMore = false;
  saving = false;
  editing: Fornecedor | null = null;
  novoNome = '';
  novoCnpj = '';
  novaLoja = '';
  page = 1;
  readonly pageSize = 10;
  hasMore = false;
  // Termo da barra de pesquisa. Aplicado no BACKEND (LIKE em codigo, loja,
  // CNPJ e nome): filtrar o array local so encontraria os 10 registros da
  // pagina ja carregada, e o cadastro tem muito mais que isso.
  busca = '';

  get tableHeight(): number {
    return Math.max(window.innerHeight - 280, 300);
  }

  fornecedores: Fornecedor[] = [];
  fornecedoresFiltered: Fornecedor[] = [];

  readonly cnpjMask = CNPJ_MASK;
  readonly cnpjPattern = CNPJ_PATTERN;

  readonly pageActions: Array<PoPageAction> = [
    { label: 'Adicionar', action: () => this.openForm(), icon: 'an an-plus' }
  ];

  readonly filterSettings: PoPageFilter = {
    action: this.filterAction.bind(this),
    placeholder: 'Pesquisar fornecedores...'
  };

  readonly columns: Array<PoTableColumn> = [
    { property: 'codigo', label: 'Código', width: '120px', type: 'link', action: (_: string, row: Fornecedor) => this.editar(row) },
    { property: 'loja',   label: 'Loja',   width: '80px' },
    { property: 'cnpj',   label: 'CNPJ',   width: '180px' },
    { property: 'nome',   label: 'Fornecedor' }
  ];

  readonly tableActions: Array<PoTableAction> = [
    { label: 'Editar',  icon: 'po-icon-edit',   action: (row: Fornecedor) => this.editar(row) },
    { label: 'Excluir', icon: 'po-icon-delete', action: (row: Fornecedor) => this.confirmarExclusao(row), type: 'danger' }
  ];

  readonly modalPrimaryAction: PoModalAction = {
    action: () => this.salvar(),
    label: 'Salvar'
  };

  readonly modalSecondaryAction: PoModalAction = {
    action: () => this.formModal.close(),
    label: 'Cancelar'
  };

  constructor(
    private service: FornecedoresOcultoService,
    private notif: PoNotificationService,
    private dialog: PoDialogService
  ) {}

  ngOnInit(): void {
    this.carregar();
  }

  carregar(): void {
    this.page = 1;
    this.fornecedores = [];
    this.fornecedoresFiltered = [];
    this.hasMore = false;
    this.loading = true;
    this.service.list(this.page, this.pageSize, this.busca).subscribe({
      next: res => {
        this.fornecedores = res.data;
        this.fornecedoresFiltered = [...res.data];
        this.hasMore = res.hasNext;
        this.loading = false;
      },
      error: () => {
        this.notif.error('Erro ao carregar fornecedores ocultos.');
        this.loading = false;
      }
    });
  }

  carregarMais(): void {
    if (!this.hasMore || this.loadingMore) return;
    this.loadingMore = true;
    this.page++;
    // Mantem o termo: sem ele o "Carregar mais" de uma busca traria a
    // proxima pagina da lista COMPLETA, misturando registros fora do filtro.
    this.service.list(this.page, this.pageSize, this.busca).subscribe({
      next: res => {
        this.fornecedores = [...this.fornecedores, ...res.data];
        this.fornecedoresFiltered = [...this.fornecedores];
        this.hasMore = res.hasNext;
        this.loadingMore = false;
      },
      error: () => {
        this.notif.error('Erro ao carregar mais fornecedores.');
        this.loadingMore = false;
        this.page--;
      }
    });
  }

  openForm(): void {
    this.editing = null;
    this.novoNome = '';
    this.novoCnpj = '';
    this.novaLoja = '';
    this.formModal.open();
  }

  editar(f: Fornecedor): void {
    this.editing = f;
    this.novoNome = f.nome;
    this.novoCnpj = f.cnpj;
    this.novaLoja = f.loja;
    this.formModal.open();
  }

  salvar(): void {
    const nome = this.novoNome.trim();
    const cnpj = this.novoCnpj.trim();
    const loja = (this.novaLoja ?? '').trim().padStart(4, '0');

    // No create, nome eh opcional (vem do SA2 se vazio). No edit, eh obrigatorio.
    if (!cnpj || !loja) {
      this.notif.warning('Preencha CNPJ e Loja.');
      return;
    }
    if (this.editing && (!nome || nome.length < 3)) {
      this.notif.warning('Nome obrigatorio (minimo 3 caracteres).');
      return;
    }
    if (nome && nome.length < 3) {
      this.notif.warning('O nome do fornecedor deve ter no minimo 3 caracteres.');
      return;
    }
    if (!isValidCnpj(cnpj)) {
      this.notif.warning('CNPJ invalido. Verifique o número informado.');
      return;
    }
    if (!/^\d{4}$/.test(loja)) {
      this.notif.warning('Loja invalida (use ate 4 digitos).');
      return;
    }

    this.saving = true;

    const op$ = this.editing?.codigo
      ? this.service.update({ codigo: this.editing.codigo, loja, nome })
      : this.service.create({ cnpj, loja, nome: nome || undefined });

    const wasEditing = !!this.editing;

    op$.subscribe({
      next: () => {
        this.saving = false;
        this.formModal.close();
        this.notif.success(wasEditing ? 'Fornecedor atualizado.' : 'Fornecedor adicionado.');
        this.carregar();
      },
      error: err => {
        this.saving = false;
        this.notif.error(err?.error?.message ?? 'Erro ao salvar fornecedor.');
      }
    });
  }

  confirmarExclusao(f: Fornecedor): void {
    if (!f.codigo || !f.loja) return;
    this.dialog.confirm({
      title: 'Excluir fornecedor',
      message: `Confirma a exclusão do fornecedor <b>${f.nome}</b> (${f.cnpj} - loja ${f.loja})?`,
      confirm: () => this.excluir(f.codigo!, f.loja)
    });
  }

  private excluir(codigo: string, loja: string): void {
    this.service.delete(codigo, loja).subscribe({
      next: () => {
        this.notif.success('Fornecedor excluido.');
        this.carregar();
      },
      error: err => {
        this.notif.error(err?.error?.message ?? 'Erro ao excluir fornecedor.');
      }
    });
  }

  private filterAction(filter: string): void {
    // Consulta a API de novo com o termo (mesmo padrao do notas-list). Antes
    // isto filtrava apenas o array ja em memoria, entao a pesquisa "nao
    // achava" qualquer fornecedor que ainda nao tivesse sido paginado.
    this.busca = (filter || '').trim();
    this.carregar();
  }
}
